// usePushSubscribe — once the learner is signed in AND has granted
// notification permission, fetch the FCM token and write it onto
// /users/{userId}.fcmToken so the Cloud Function can target this device.
//
// Eng-D1: re-run getToken() every signed-in transition (token can rotate)
// and diff against Firestore before writing.
// Eng-D4 (Codex C1): bail early when isSupported(), permission, SW, or
// vapidKey is missing — never call getToken with bad inputs.
//
// We pass `serviceWorkerRegistration: undefined` and let FCM use the default
// SW (Workbox's /sw.js, which also hosts our messaging.onBackgroundMessage
// handler — see apps/user/src/sw.ts). We used to register a separate
// /firebase-messaging-sw.js but that produced double notifications on iOS
// PWA because both SWs were active in the root scope.
//
// 2026-05-27 — PUSH_RESET_VERSION:活動前我們清空了 /users/*.fcmToken 並
// 希望「全員重新走啟用通知一次」、確認 token 都活著。瀏覽器原生
// Notification.permission 不能從程式 reset(已 granted 不會再跳系統權限框)、
// 但我們可以用 localStorage 旗標強迫每位學員至少跑一次 getToken() 寫回
// Firestore,沒對到旗標就回傳 false(讓 PushHint 顯示「· 點 此 允 許 通 知」)。

import { getFirebase, userDoc, usersCol } from "@goodbye/firebase";
import {
  getMessaging,
  getToken,
  isSupported,
} from "firebase/messaging";
import {
  deleteField,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { useEffect } from "react";
import { env } from "../env";
import type { PushPermission } from "./usePushPermission";

/** 活動前(2026-05-27)強制重新訂閱通知 — bump 這個版本後、所有學員
 *  下次開啟學員端會被當成「沒啟用過」、PushHint 重新顯示「· 點 此 允 許」
 *  按鈕(即使瀏覽器 Notification.permission 還是 granted)。
 *  按下後 hook 會 force re-getToken + 寫回 Firestore + 蓋上 localStorage 旗標。 */
export const PUSH_RESET_VERSION = "2026-05-27-dedup";
const PUSH_RESET_KEY = "echo-push-reset-v";

/** 判斷此裝置 / 此瀏覽器 (per origin) 有沒有跑過當前版本的訂閱流程。
 *  Home 用這個值決定 PushHint 該顯示「已開」還是「點此允許」。 */
export function hasFreshPushSubscription(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(PUSH_RESET_KEY) === PUSH_RESET_VERSION;
}

function markFreshPushSubscription(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(PUSH_RESET_KEY, PUSH_RESET_VERSION);
  } catch {
    /* localStorage might be full / blocked — fail open */
  }
}

/** Standalone subscribe — manually triggered (e.g. from PushHint button)、
 *  不依賴 React 生命週期,適用於「使用者按下『點此允許通知』、permission
 *  原本就 granted、useEffect 不會重跑」的場景。
 *
 *  回傳 true 代表「拿到 token 且 Firestore 已是最新」(無論這次有沒有實際
 *  寫入)。Home.tsx 用這個訊號直接關 modal、不再依賴 localStorage flag
 *  polling — iOS Safari standalone PWA 對 localStorage.setItem 偶有 silent
 *  failure、會讓 modal 永遠關不掉、但 push 通知其實已經正常運作。 */
export async function subscribePushNow(userId: string): Promise<boolean> {
  if (!env.vapidKey) return false;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  try {
    const ok = await isSupported();
    if (!ok) return false;
    const swReg = await navigator.serviceWorker.ready;
    const messaging = getMessaging(getFirebase().app);
    const token = await getToken(messaging, {
      vapidKey: env.vapidKey,
      serviceWorkerRegistration: swReg,
    });
    if (!token) return false;
    const ref = userDoc(userId);
    const snap = await getDoc(ref);
    const existing = snap.exists()
      ? (snap.data() as { fcmToken?: string }).fcmToken
      : undefined;
    const fresh = hasFreshPushSubscription();
    if (existing === token && fresh) return true;

    // Device-token uniqueness: a single PWA install / browser pair gets
    // ONE token from FCM. If the same device was previously signed in as
    // a different learner (test scenarios, family sharing a phone), that
    // learner's user doc still carries this token — every push targeted
    // at THIS learner is silently delivered to the OLD learner's device
    // too (Stage 6 bug reported 2026-05-27). Clear the token from any
    // OTHER user doc before we claim it for the current userId.
    try {
      const dupes = await getDocs(
        query(usersCol(), where("fcmToken", "==", token)),
      );
      const stale = dupes.docs.filter((d) => d.id !== userId);
      if (stale.length > 0) {
        await Promise.allSettled(
          stale.map((d) => updateDoc(d.ref, { fcmToken: deleteField() })),
        );
      }
    } catch (err) {
      // Non-fatal: even if the cleanup query fails (rules / network),
      // proceed to write the new owner so the current learner can still
      // receive pushes. Cross-user leakage may persist for that case.
      if (typeof console !== "undefined") {
        console.warn("[subscribePushNow] dedup cleanup failed", err);
      }
    }

    await updateDoc(ref, { fcmToken: token });
    markFreshPushSubscription();
    return true;
  } catch (err) {
    if (typeof console !== "undefined") {
      console.warn("[subscribePushNow]", err);
    }
    return false;
  }
}

export function usePushSubscribe(
  userId: string | null,
  permission: PushPermission,
): void {
  useEffect(() => {
    if (!userId) return;
    if (permission !== "granted") return;
    let cancelled = false;
    void subscribePushNow(userId).catch(() => {
      /* already swallowed inside subscribePushNow */
    });
    return () => {
      cancelled = true;
      void cancelled; // silence unused
    };
  }, [userId, permission]);
}
